// Tenant Content Service for frontend/user-classic
import { getCdnBaseUrl } from '../utils/cdnUtils'
import { getApiBaseUrl, withApiBaseUrl } from '../lib/apiBaseUrl'

const TENANT_CONTENT_FETCH_TIMEOUT_MS = 5_000
const TENANT_CONTENT_CACHE_TTL_MS = 60_000

type TenantContentSource =
    | 'configuration-service'
    | 'default-fallback'
    | 'cache'

interface TenantContentCacheEntry {
    response: TenantContentResponse
    expiresAt: number
}

const tenantContentCache = new Map<string, TenantContentCacheEntry>()
const tenantContentInflight = new Map<string, Promise<TenantContentResponse>>()

const fetchWithTimeout = async (
    input: RequestInfo | URL,
    init: RequestInit,
    timeoutMs: number
): Promise<Response> => {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)

    try {
        return await fetch(input, { ...init, signal: controller.signal })
    } finally {
        window.clearTimeout(timeoutId)
    }
}

export interface TenantContentResponse {
    success: boolean
    source?: TenantContentSource
    data: {
        tenantId: string
        name: string
        description: string
        frontendTemplate?: string
        branding: {
            primaryColor: string
            secondaryColor: string
            logo: string
            favicon: string
        }
        bankList?: Array<Record<string, unknown>>
        content: {
            siteTitle?: string
            marqueeText: string
            welcomeMessage: string
            contactInfo: {
                line: string
                phone: string
                email: string
            }
            socialLinks: {
                facebook: string
                instagram: string
                twitter: string
            }
        }
        /** เปิด/ปิด Web Chat และ session settings (จาก Configuration Service) */
        features?: {
          webChatEnabled?: boolean
          wipWupMinutes?: number
          wipWupMinutesOther?: number
          sessionIdleMinutes?: number
          depositEnabled?: boolean
          withdrawEnabled?: boolean
          lotteryEnabled?: boolean
          casinoEnabled?: boolean
          depositAutoCreditEnabled?: boolean
          depositAutoCreditViaPayment?: boolean
          depositAutoCreditViaSlip?: boolean
          depositAutoCreditViaSms?: boolean
        }
    }
}

interface GetTenantContentOptions {
    forceFresh?: boolean
}

const getTenantContentCacheKey = (tenantId: string) => tenantId.trim().toLowerCase()

const cloneTenantContentResponse = (
    response: TenantContentResponse,
    source: TenantContentSource = response.source || 'configuration-service'
): TenantContentResponse => ({
    ...response,
    source,
    data: {
        ...response.data,
        branding: { ...response.data.branding },
        content: {
            ...response.data.content,
            contactInfo: { ...response.data.content.contactInfo },
            socialLinks: { ...response.data.content.socialLinks },
        },
        features: response.data.features ? { ...response.data.features } : undefined,
        bankList: Array.isArray(response.data.bankList)
            ? response.data.bankList.map((item) => ({ ...item }))
            : response.data.bankList,
    }
})

const getCachedTenantContent = (tenantId: string): TenantContentResponse | null => {
    const cached = tenantContentCache.get(tenantId)
    if (!cached || cached.expiresAt <= Date.now()) {
        tenantContentCache.delete(tenantId)
        return null
    }
    return cloneTenantContentResponse(cached.response, 'cache')
}

const setCachedTenantContent = (tenantId: string, response: TenantContentResponse): void => {
    tenantContentCache.set(tenantId, {
        response: cloneTenantContentResponse(response),
        expiresAt: Date.now() + TENANT_CONTENT_CACHE_TTL_MS,
    })
}

const buildTenantContentUrl = (baseURL: string, tenantId: string, forceFresh = false): string => {
    const params = new URLSearchParams()
    if (forceFresh) {
        params.set('_fresh', '1')
    }

    const query = params.toString()
    return `${baseURL}/api/v1/configuration-service/tenant-content/${encodeURIComponent(tenantId)}${query ? `?${query}` : ''}`
}

/**
 * Get tenant content from API
 */
export const getTenantContent = async (
    tenantId: string,
    options: GetTenantContentOptions = {}
): Promise<TenantContentResponse> => {
    const normalizedTenantId = getTenantContentCacheKey(tenantId)
    const forceFresh = options.forceFresh === true

    if (!forceFresh) {
        const cached = getCachedTenantContent(normalizedTenantId)
        if (cached) {
            return cached
        }
    } else {
        tenantContentCache.delete(normalizedTenantId)
    }

    const inflightKey = `${normalizedTenantId}:${forceFresh ? 'fresh' : 'default'}`
    const existingRequest = tenantContentInflight.get(inflightKey)
    if (existingRequest) {
        return existingRequest
    }

    const request = (async () => {
        try {
            const baseURL = getApiBaseUrl()
            const configResponse = await fetchWithTimeout(
                buildTenantContentUrl(baseURL, normalizedTenantId, forceFresh),
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Tenant-ID': normalizedTenantId,
                        'X-Tenant-Domain': window.location.hostname,
                        ...(forceFresh ? {
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache',
                        } : {}),
                    }
                },
                TENANT_CONTENT_FETCH_TIMEOUT_MS
            )

            if (configResponse.ok) {
                const data = (await configResponse.json()) as TenantContentResponse
                const response: TenantContentResponse = {
                    ...data,
                    source: 'configuration-service',
                }

                setCachedTenantContent(normalizedTenantId, response)
                console.log('✅ Tenant content loaded from Configuration Service (shared cache enabled)')
                return response
            }

            throw new Error(`Configuration-service tenant content request failed with status ${configResponse.status}`)
        } catch (error) {
            console.error('Failed to fetch tenant content:', error)

            const cdnBaseUrl = getCdnBaseUrl()
            return {
                success: true,
                source: 'default-fallback' as const,
                data: {
                    tenantId: normalizedTenantId,
                    name: 'Lenhuay',
                    description: 'เว็บหวยออนไลน์ที่มาแรงที่สุด มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย',
                    branding: {
                        primaryColor: '#00a16f',
                        secondaryColor: '#004c3d',
                        logo: `${cdnBaseUrl}/Lenhuay/img/logo.png`,
                        favicon: `${cdnBaseUrl}/Lenhuay/img/favicon.ico`
                    },
                    content: {
                        marqueeText: 'ยินดีต้อนรับทุกท่านเข้าสู่เว็บ lenhuay.net เว็บหวยออนไลน์ที่มาแรงที่สุดในตอนนี้ มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย',
                        welcomeMessage: 'เรายินดีต้อนรับทุกท่านเข้าสู่ระบบแทงหวยออนไลน์ที่มั่นคงที่สุด กดที่ ICON LINE ได้เลย เรามีพนักงานรอต้อนรับลูกค้าตลอด 24 ชั่วโมง',
                        contactInfo: {
                            line: '@lenhuay',
                            phone: '02-xxx-xxxx',
                            email: 'support@lenhuay.net'
                        },
                        socialLinks: {
                            facebook: 'https://facebook.com/lenhuay',
                            instagram: 'https://instagram.com/lenhuay',
                            twitter: 'https://twitter.com/lenhuay'
                        }
                    },
                    features: { webChatEnabled: true }
                }
            }
        } finally {
            tenantContentInflight.delete(inflightKey)
        }
    })()

    tenantContentInflight.set(inflightKey, request)
    return request
}

/**
 * Update tenant content (for future use)
 */
export const updateTenantContent = async (
    tenantId: string,
    content: Partial<TenantContentResponse['data']['content']>
): Promise<TenantContentResponse> => {
    try {
        const response = await fetch(withApiBaseUrl('/api/v1/tenant-management/tenants/content'), {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                tenantId,
                content
            })
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        return await response.json()
    } catch (error) {
        console.error('Failed to update tenant content:', error)
        throw error
    }
}
